import { useMemo, useState } from 'react'

import { Button } from '@/components/ui/Button'
import { Field, Input, Select } from '@/components/ui/Field'
import { Spinner } from '@/components/ui/Spinner'
import { formatDistance } from '@/lib/format'
import { useSaveVenue, useVenues } from './queries'

/**
 * Choosing a venue from the library, or adding one on the spot.
 *
 * A plain select rather than a combobox: the library holds tens of venues, not
 * thousands, and a native select is keyboard-navigable, type-to-jump, and
 * correct on a phone without a line of JavaScript. When the venue is genuinely
 * new, the inline form adds it without leaving the enquiry.
 *
 * The distance shown next to each name is the point of the whole thing, and an
 * unmeasured venue says so rather than showing a blank.
 */
export function VenuePicker({
  value,
  onChange,
  suggestedName,
  label = 'Venue',
}: {
  value: string | null
  onChange: (venueId: string | null) => void
  /** Pre-fills the "add a venue" form — usually what the couple typed. */
  suggestedName?: string | null
  label?: string
}) {
  const venuesQuery = useVenues()
  const save = useSaveVenue()

  const [adding, setAdding] = useState(false)
  const [name, setName] = useState(suggestedName?.trim() ?? '')
  const [town, setTown] = useState('')
  const [distance, setDistance] = useState('')

  const venues = useMemo(() => venuesQuery.data ?? [], [venuesQuery.data])

  async function addVenue(event: React.FormEvent) {
    event.preventDefault()

    const parsed = distance.trim() === '' ? null : Number(distance)

    const venue = await save.mutateAsync({
      values: {
        name: name.trim(),
        town: town.trim() || null,
        distance_km: parsed !== null && Number.isFinite(parsed) ? parsed : null,
        distance_source: 'manual',
      },
    })

    onChange(venue.id)
    setAdding(false)
    setName('')
    setTown('')
    setDistance('')
  }

  if (adding) {
    return (
      <form onSubmit={(event) => void addVenue(event)} className="flex flex-col gap-4 card p-4">
        <p className="label">Add a venue</p>

        <Field label="Name" required>
          <Input value={name} onChange={(event) => setName(event.target.value)} required autoFocus />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Town" hint="Shown on the board.">
            <Input value={town} onChange={(event) => setTown(event.target.value)} />
          </Field>

          <Field
            label="One-way distance"
            aside="km"
            hint="From Somerset West. Leave blank and add it later."
          >
            <Input
              type="number"
              inputMode="decimal"
              step="0.1"
              min="0"
              value={distance}
              onChange={(event) => setDistance(event.target.value)}
            />
          </Field>
        </div>

        <div className="flex gap-2">
          <Button type="submit" variant="primary" size="sm" loading={save.isPending} loadingLabel="Adding" disabled={!name.trim()}>
            Add venue
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setAdding(false)}>
            Cancel
          </Button>
        </div>
      </form>
    )
  }

  return (
    <div className="flex max-w-md flex-col gap-2">
      <Field label={label}>
        {venuesQuery.isPending ? (
          <div className="flex h-9 items-center gap-2 text-taupe">
            <Spinner size={12} />
            <span className="label">Loading venues</span>
          </div>
        ) : (
          <Select value={value ?? ''} onChange={(event) => onChange(event.target.value || null)}>
            <option value="">Not matched to a venue</option>
            {venues.map((venue) => (
              <option key={venue.id} value={venue.id}>
                {venue.name}
                {venue.town ? ` — ${venue.town}` : ''}
                {venue.distance_km === null ? ' (not measured)' : ` (${formatDistance(Number(venue.distance_km))})`}
              </option>
            ))}
          </Select>
        )}
      </Field>

      <Button
        variant="ghost"
        size="sm"
        className="-ml-2.5 self-start"
        onClick={() => {
          setName(suggestedName?.trim() ?? '')
          setAdding(true)
        }}
      >
        Add a new venue
      </Button>
    </div>
  )
}
